/* eslint-disable use-encapsulation/prefer-custom-hooks */
import { MbeArrowDropdown } from '@/components/icons';
import type { IAddress } from '@/types';
import { FormControl, FormLabel, InputGroup, Select } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ErrorMessage, MbeInput } from '@/components/shared';
import { countryListAlpha2, ERRORS } from '@/constants';
import { useValidator } from '@/features/users';
import { provincesList } from '@/constants/provinces';
import { useJsApiLoader } from '@react-google-maps/api';
import { useEffect, useState } from 'react';

interface IAddressFieldsProps {
  onCountryChange?: () => void;
}

export const AddressFields: React.FC<IAddressFieldsProps> = ({ onCountryChange }): JSX.Element => {
  const { t } = useTranslation();
  const { validateZipCode } = useValidator();

  // Workaround for performance warning issue
  const [libraries] = useState<
    ('places' | 'drawing' | 'geometry' | 'localContext' | 'visualization')[]
  >(['places']);

  const { isLoaded, loadError: hasError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '',
    libraries,
  });

  const inputAddress: HTMLInputElement | null = document.querySelector('#streetAddress');

  const autoCompleteInit = (): google.maps.places.Autocomplete => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return new google.maps.places.Autocomplete(inputAddress ?? new HTMLInputElement());
  };

  useEffect(() => {
    if (!isLoaded) return;

    const autocomplete = autoCompleteInit();

    autocomplete.addListener('place_changed', async (): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      const place = await autocomplete.getPlace();

      if (place.address_components && place.name) {
        for (const component of place.address_components) {
          const type = component.types[0];

          switch (type) {
            case 'street_number':
              setValue('streetAddress', place.name);
              break;
            case 'administrative_area_level_2':
              setValue('province', component.short_name);
              break;
            case 'administrative_area_level_3':
              setValue('city', component.long_name);
              break;
            case 'postal_town':
              setValue('city', component.long_name);
              break;
            case 'locality':
              setValue('city', component.long_name);
              break;
            case 'country':
              setValue('countryCode', component.short_name);
              break;
            case 'postal_code':
              setValue('zipCode', component.long_name);
              break;
            default:
              break;
          }
        }
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, hasError]);

  const {
    formState: { errors },
    getValues,
    register,
    setValue,
  } = useFormContext<IAddress>();

  const countryCodeValue = getValues('countryCode') && getValues('countryCode').toLowerCase();

  const zipCodeProps = register('zipCode', {
    validate: (value) => validateZipCode({ countryCode: countryCodeValue, zipCode: value }),
  });

  const provincePlaceholderText = t('FORM.PROVINCE');
  return (
    <>
      <MbeInput
        error={errors.streetAddress}
        id="streetAddress"
        placeholder={t('FORM.STREET_ADDRESS')}
        {...register('streetAddress', {
          required: { value: true, message: t(ERRORS.empty) },
        })}
      />
      <MbeInput
        error={errors.city}
        id="city"
        placeholder={t('FORM.CITY')}
        {...register('city', {
          required: { value: true, message: t(ERRORS.empty) },
        })}
      />
      <FormControl variant="floating">
        <InputGroup>
          <Select
            icon={<MbeArrowDropdown color="brand.red.light" />}
            id="countryCode"
            isInvalid={!!errors.countryCode}
            placeholder={t('FORM.COUNTRY_CODE')}
            variant="mbe"
            {...register('countryCode', {
              onChange: () => {
                onCountryChange?.();
                setValue('province', '');
              },
              required: { value: true, message: t(ERRORS.empty) },
            })}
          >
            {Object.keys(countryListAlpha2).map((k) => {
              const key = k as keyof typeof countryListAlpha2;
              return (
                <option key={k} value={k}>
                  {countryListAlpha2[key]}
                </option>
              );
            })}
          </Select>
          <FormLabel htmlFor="countryCode">{t('FORM.COUNTRY_CODE')}</FormLabel>
        </InputGroup>
        {errors.countryCode?.message && <ErrorMessage message={errors.countryCode.message} />}
      </FormControl>

      {countryCodeValue in provincesList ? (
        <FormControl variant="floating">
          <InputGroup>
            <Select
              icon={<MbeArrowDropdown color="brand.red.light" />}
              id="province"
              isInvalid={!!errors.province}
              placeholder={provincePlaceholderText}
              variant="mbe"
              {...register('province', {
                required: { value: true, message: t(ERRORS.empty) },
              })}
            >
              {provincesList[countryCodeValue as keyof typeof provincesList].map(({ id, name }) => {
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </Select>
            <FormLabel htmlFor="province">{provincePlaceholderText}</FormLabel>
          </InputGroup>
          {errors.province?.message && <ErrorMessage message={errors.province.message} />}
        </FormControl>
      ) : (
        <MbeInput
          error={errors.province}
          id="province"
          placeholder={provincePlaceholderText}
          {...register('province', {
            required: { value: true, message: t(ERRORS.empty) },
          })}
        />
      )}

      <MbeInput
        error={errors.zipCode}
        id="zipCode"
        placeholder={t('FORM.ZIP_CODE')}
        {...zipCodeProps}
      />
    </>
  );
};
